// context/RemoteConfigContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { RemoteConfig } from "firebase/remote-config";
import {
    remoteConfig,
    fetchAndActivate,
    readRemoteConfigValue,
    ReadConfigFormat
} from "@vahak/core/dist/_services/firebase.service";

type ReadMethod = <T>(keyName: string, format: ReadConfigFormat, fallback?: T) => T;

type Context = {
    readValue: ReadMethod;
    reloadConfig: Function;
    isLoaded: boolean;
};

const RemoteConfigContext = createContext({
    readValue: () => {
        throw new Error("RemoteConfig not initialized");
    },
    reloadConfig: () => {},
    isLoaded: false
} as Context);

export const RemoteConfigProvider = ({ children }: { children: ReactNode }) => {
    const [configInstance, setConfigInstance] = useState<RemoteConfig | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    const reloadConfig = async () => {
        try {
            await fetchAndActivate(remoteConfig!);
            setConfigInstance(remoteConfig);
            setIsLoaded(true);
        } catch (err) {
            console.error("Failed to activate remote config", err);
        }
    };

    useEffect(() => {
        !!remoteConfig && reloadConfig();
    }, [remoteConfig]);

    const readValue: ReadMethod = (keyName, format, fallback) =>
        readRemoteConfigValue(configInstance, keyName, format, fallback);

    return (
        <RemoteConfigContext.Provider value={{ readValue, reloadConfig, isLoaded }}>
            {children}
        </RemoteConfigContext.Provider>
    );
};

export const useRemoteConfig = () => useContext(RemoteConfigContext);
